/**
 * Theme Config Hooks
 *
 * React Query hooks for theme and branding configuration.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import {
  ThemeConfigService,
  type ThemeConfig,
  type UpdateThemeConfigInput,
} from '../services/theme-config.service';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const themeConfigKeys = {
  all: ['themeConfig'] as const,
  config: () => [...themeConfigKeys.all, 'config'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Fetch the active theme configuration for the current tenant.
 */
export function useThemeConfig() {
  const client = useApiClient();
  const { isSignedIn, token } = useAuth();
  const service = new ThemeConfigService(client);

  return useQuery<ThemeConfig, Error>({
    queryKey: themeConfigKeys.config(),
    queryFn: () => service.getThemeConfig(),
    staleTime: 1000 * 60 * 10, // 10 minutes
    enabled: isSignedIn && !!token,
  });
}

/**
 * Update (upsert) the theme configuration.
 * Automatically invalidates the theme config query on success.
 */
export function useUpdateThemeConfig() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const service = new ThemeConfigService(client);

  return useMutation<ThemeConfig, Error, UpdateThemeConfigInput>({
    mutationFn: (input) => service.updateThemeConfig(input),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(themeConfigKeys.config(), updatedConfig);
    },
  });
}
